import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useAuth } from "../../auth/AuthProvider";
import {
  deleteOffset,
  fetchLoadAirsheds,
  fetchLoadIntensityTiles,
  getLoad,
  getLoadPlants,
  queueLoadRun,
} from "../../api/loads";
import type {
  LoadDetail as LoadDetailType,
  LoadOffset,
} from "../../features/load/domain/types";
import { LoadSpatialHeatmap } from "../../features/load/components/LoadSpatialHeatmap";
import { LoadOffsetModal } from "../../features/load/components/LoadOffsetModal";
import {
  PageLayout,
  PageHeader,
  Section,
  Card,
  Button,
  PageSpinner,
  Badge,
} from "../../components";
import { LoadGridSupplyPanel } from "../../features/load-portfolio/components/LoadGridSupplyPanel";
import { LoadImpactSummaryCard } from "../../features/load-portfolio/components/LoadImpactSummaryCard";
import { LoadServingStatsCard } from "../../features/load/components/LoadServingStatsCard";
import { LoadImpactExplorer } from "../../features/load-portfolio/components/LoadImpactExplorer";
import { LoadEmissionsCard } from "../../features/load-portfolio/components/LoadEmissionsCard";
import { RangeModeProvider } from "../../features/impact/context/RangeModeContext";
import { RangeModeToggle } from "../../features/impact/components/RangeModeToggle";
import { LoadRangeDashboard } from "../../features/load-range-dashboard/LoadRangeDashboard";
import { triggerLoadSweep } from "../../features/impact/api/sensitivity";
import type { LoadImpactSummary, DispatchSummary } from "../../features/load-portfolio/domain/types";
import {
  VersionStamp,
  StalenessBadge,
  RerunConfirmDialog,
} from "../../features/model-version";

const REFETCH_RUNNING_MS = 5_000;

const FUEL_COLOR: Record<string, string> = {
  coal: "text-zinc-300",
  ng: "text-amber-300",
  oil: "text-orange-300",
};

function SpatialAllocationCard({ load }: { load: LoadDetailType }) {
  const { token } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["load-plants", load.load_id],
    queryFn: () => getLoadPlants(load.load_id, token!),
    enabled: !!token,
  });

  // L4 dispersion: InMAP airshed polygons keyed on the load's displaced plants.
  // Static per run — cache generously. Falls back to no-overlay if not warmed
  // yet (the heatmap still shows plant markers).
  const { data: airsheds } = useQuery({
    queryKey: ["load-airsheds", load.load_id],
    queryFn: () => fetchLoadAirsheds(load.load_id, token!),
    enabled: !!token,
    staleTime: 60 * 60_000,
    gcTime: 2 * 60 * 60_000,
    retry: 0,
  });

  // Phase 4b rasterized intensity tiles — built by rebuild_load at the end
  // of each load run and uploaded to S3. Null when not yet built; the map
  // gracefully falls back to the airshed polygon layer.
  const { data: intensityTiles } = useQuery({
    queryKey: ["load-intensity-tiles", load.load_id],
    queryFn: () => fetchLoadIntensityTiles(load.load_id, token!),
    enabled: !!token,
    staleTime: 60 * 60_000,
    gcTime: 2 * 60 * 60_000,
    retry: 0,
  });

  const hasPlants = !!data && data.count > 0;

  return (
    <>
      {/* Map heatmap — the "where the impacts come from" visual */}
      {hasPlants && (
        <Section>
          <LoadSpatialHeatmap
            loadId={load.load_id}
            loadLatitude={load.latitude}
            loadLongitude={load.longitude}
            loadName={load.load_name}
            plants={data!.plants}
            scenarioYear={data!.scenario_year}
            airsheds={airsheds}
            intensityTiles={intensityTiles}
          />
        </Section>
      )}

      {/* Top-N table beneath the map */}
      <Section>
        <div className="bg-surface-700 rounded-lg shadow border border-border overflow-hidden">
          <div className="px-6 pt-5 pb-2">
            <h2 className="text-lg font-medium text-text-primary">
              Top Contributors
              <span className="block text-xs font-normal text-text-muted mt-0.5">
                Per-plant attribution of the residual grid load (same engine path RE
                projects use — these are the actual generators your load made the
                grid ramp).
              </span>
            </h2>
          </div>
          <div className="px-6 pb-5 pt-2">
            {isLoading && <div className="text-sm text-text-muted">Loading…</div>}
            {!isLoading && !hasPlants && (
              <div className="text-sm text-text-muted">
                No per-plant attribution available for this run yet (engine may
                have skipped the spatial step if no marginal mix was attributed).
              </div>
            )}
            {hasPlants && (
              <>
                <div className="text-xs text-text-muted mb-2">
                  {data!.count} plant{data!.count !== 1 ? "s" : ""} attributed
                  {data!.scenario_year ? ` for grid year ${data!.scenario_year}` : ""}.
                </div>
                <table className="w-full text-xs">
                  <thead className="text-text-muted">
                    <tr>
                      <th className="text-left pb-2">Plant</th>
                      <th className="text-left pb-2">Fuel</th>
                      <th className="text-left pb-2">Location</th>
                      <th className="text-right pb-2">Capacity</th>
                      <th className="text-right pb-2">Consumed (MWh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.plants.slice(0, 12).map((p, i) => (
                      <tr key={`${p.plant_id}-${p.fuel_type}-${i}`} className="border-t border-border">
                        <td className="py-1 text-text-primary">{p.plant_name || p.plant_id}</td>
                        <td className={`py-1 ${FUEL_COLOR[p.fuel_type] || "text-text-secondary"}`}>
                          {p.fuel_type}
                        </td>
                        <td className="py-1 text-text-secondary">
                          {[p.county, p.state].filter(Boolean).join(", ")}
                        </td>
                        <td className="py-1 text-right text-text-secondary">
                          {p.capacity_mw ? `${p.capacity_mw.toFixed(0)} MW` : "—"}
                        </td>
                        <td className="py-1 text-right text-text-primary font-medium">
                          {p.consumed_mwh.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data!.count > 12 && (
                  <div className="text-xs text-text-muted mt-2">
                    …and {data!.count - 12} more. Full per-plant attribution
                    persisted in load_plant_consumption.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Section>

    </>
  );
}

export default function LoadDetail() {
  const { loadId } = useParams<{ loadId: string }>();
  const { token } = useAuth();
  const qc = useQueryClient();

  // Result view switcher: Classic (existing layout) | Range dashboard (new
  // opt-in alternate view). Persisted in the URL, mirrors ProjectDetail.
  const [searchParams, setSearchParams] = useSearchParams();
  const dashView = searchParams.get("view") === "range" ? "range" : "classic";
  const setDashView = (v: "classic" | "range") => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === "range") next.set("view", "range");
        else next.delete("view");
        return next;
      },
      { replace: true },
    );
  };
  // Top-level impact-basis lens for the Range dashboard (drives the whole page).
  const [loadLens, setLoadLens] = useState<"consequential" | "attributional">(
    "consequential",
  );

  const [showOffsets, setShowOffsets] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  // Epoch-ms deadline: while now < this, auto-poll so the Range view notices the
  // sweep finishing without a manual refresh (sweep ≠ a model run, so run-status
  // polling alone never catches it).
  const [sweepWatchUntil, setSweepWatchUntil] = useState(0);
  const handleRunSweep = async () => {
    if (!token || !loadId) return;
    setSweeping(true);
    try {
      await triggerLoadSweep(loadId, token);
      setSweepWatchUntil(Date.now() + 90_000);
    } catch (err) {
      console.error("Failed to enqueue load sensitivity sweep", err);
    } finally {
      setSweeping(false);
    }
  };
  const [offsetModalOpen, setOffsetModalOpen] = useState(false);
  const [editingOffset, setEditingOffset] = useState<LoadOffset | null>(null);
  const [rerunOpen, setRerunOpen] = useState(false);

  const { data: load, isLoading } = useQuery<LoadDetailType>({
    queryKey: ["load", loadId],
    queryFn: () => getLoad(loadId!, token!),
    enabled: !!loadId && !!token,
    // React Query v5: callback receives the query object, data is at
    // query.state.data (not the data directly). Auto-poll while (a) a model run
    // is queued/running, (b) a sensitivity sweep is running, or (c) we just
    // enqueued a sweep and are inside the watch window (bridges the
    // enqueue→first-row gap and covers re-runs of stale bands).
    refetchInterval: (query) => {
      const data = query.state.data;
      const runStatus = data?.latest_run?.status;
      if (runStatus === "running" || runStatus === "queued") return REFETCH_RUNNING_MS;
      if (data?.latest_run?.scenarios?.status === "running") return REFETCH_RUNNING_MS;
      if (sweepWatchUntil > Date.now()) return REFETCH_RUNNING_MS;
      return false;
    },
  });

  if (isLoading || !load) return <PageSpinner />;

  const run = load.latest_run;
  const summary = (run?.summary_json ?? null) as LoadImpactSummary | null;
  const dispatch = (run?.dispatch_summary_json ?? null) as DispatchSummary | null;
  const dailyDispatch = (dispatch as any)?.daily_dispatch;
  const isRunning = run?.status === "queued" || run?.status === "running";
  const hasResults = run?.status === "completed" && !!summary;

  const handleRun = async () => {
    if (!token || !loadId) return;
    setQueueing(true);
    try {
      await queueLoadRun(loadId, token);
      qc.invalidateQueries({ queryKey: ["load", loadId] });
    } finally {
      setQueueing(false);
    }
  };

  // Kept sections, shared by both Classic and Range views.
  const spatialSection = hasResults ? <SpatialAllocationCard load={load} /> : null;
  const offsetsSection = (
    <Section>
      <button
        type="button"
        className="w-full flex items-center justify-between text-left p-4 bg-surface-700 rounded-lg border border-border hover:bg-surface-600"
        onClick={() => setShowOffsets((v) => !v)}
      >
        <span className="text-sm font-medium text-text-primary">
          On-site Offsets (optional)
          <span className="ml-2 text-text-muted text-xs">
            {load.offsets.length} configured
          </span>
        </span>
        {showOffsets ? <FiChevronUp /> : <FiChevronDown />}
      </button>
      {showOffsets && (
        <Card className="mt-2">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-text-muted">
                {load.offsets.length === 0
                  ? "This load is fully grid-served — add on-site RE / BESS / NG to model offsetting some of the residual."
                  : "Engine treats offsets as residual reducers in front of the grid (dispatched against the load profile before residual is attributed to the grid's marginal mix)."}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditingOffset(null);
                  setOffsetModalOpen(true);
                }}
              >
                + Add Offset
              </Button>
            </div>
            {load.offsets.length > 0 && (
              <ul className="space-y-2 text-sm">
                {load.offsets.map((o) => (
                  <li
                    key={o.offset_id}
                    className="flex items-center justify-between p-2 bg-surface-600 rounded"
                  >
                    <span className="text-text-primary flex items-center gap-2">
                      {o.offset_name} <Badge>{o.source_type}</Badge>
                      <span className="text-text-muted text-xs">
                        {o.nameplate_capacity_mw} MW
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <button
                        className="text-xs text-text-secondary hover:text-text-primary"
                        onClick={() => {
                          setEditingOffset(o);
                          setOffsetModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="text-xs text-red-400 hover:text-red-300"
                        onClick={async () => {
                          if (!token) return;
                          if (!window.confirm(`Delete offset "${o.offset_name}"?`)) return;
                          await deleteOffset(load.load_id, o.offset_id, token);
                          qc.invalidateQueries({ queryKey: ["load", load.load_id] });
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      )}
    </Section>
  );

  const rangeView = hasResults && dashView === "range";

  return (
    <RangeModeProvider>
    <PageLayout>
      <PageHeader
        title={load.load_name}
        subtitle={
          (load.description ||
            [load.state, load.eia_ba_code].filter(Boolean).join(" · ")) +
          (load.existing_load_flag
            ? ` · existing (COD ${load.cod_date ? new Date(load.cod_date).getFullYear() : "n/a"})`
            : " · future")
        }
        actions={
          <div className="flex items-center gap-3">
            {hasResults && (
              <div className="inline-flex items-center bg-surface-700 border border-border rounded-lg p-0.5">
                {(["classic", "range"] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setDashView(v)}
                    aria-pressed={dashView === v}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      dashView === v
                        ? "bg-surface-500 text-text-primary shadow-sm"
                        : "text-text-muted hover:text-text-secondary"
                    }`}
                  >
                    {v === "classic" ? "Classic" : "Range dashboard"}
                  </button>
                ))}
              </div>
            )}
            {hasResults && dashView === "classic" && (
              <RangeModeToggle
                status={run?.scenarios?.status}
                onRunSweep={handleRunSweep}
                sweeping={sweeping}
              />
            )}
            <Button
              variant="primary"
              onClick={handleRun}
              disabled={queueing || isRunning}
            >
              {isRunning
                ? "Running…"
                : queueing
                ? "Queueing…"
                : "Run Assessment"}
            </Button>
          </div>
        }
      />

      {/* Status banner if running */}
      {isRunning && (
        <Section>
          <div className="bg-quantum-500/10 border border-quantum-500/30 rounded-lg px-4 py-3 text-sm text-quantum-300">
            Assessment is {run!.status} — fetching real grid data, applying the
            consequential marginal model, and computing full ReCiPe/USEtox impacts.
            This may take a minute or two.
          </div>
        </Section>
      )}

      {/* Model-version staleness — amber "re-run to update" banner */}
      {hasResults && run?.is_outdated && !isRunning && (
        <Section>
          <StalenessBadge
            isOutdated={run.is_outdated}
            currentModelVersion={run.current_model_version}
            onRerun={() => setRerunOpen(true)}
            disabled={queueing}
          />
        </Section>
      )}

      {/* Persistent, subtle "computed on {version} · {date}" stamp */}
      {hasResults && run?.result_model_version && (
        <Section>
          <VersionStamp
            resultModelVersion={run.result_model_version}
            date={run.completed_at || run.created_at}
          />
        </Section>
      )}

      {/* ——— Range dashboard (opt-in) — consolidated hero + lens-everywhere ——— */}
      {rangeView && (
        <Section>
          <LoadRangeDashboard
            loadName={load.load_name}
            summary={summary!}
            dispatch={dispatch}
            dailyDispatch={dailyDispatch || []}
            baCode={load.eia_ba_code}
            analysisYear={summary?.grid_supply?.meta?.analysis_year ?? undefined}
            scenarios={run?.scenarios}
            lens={loadLens}
            setLens={setLoadLens}
            onRunSweep={handleRunSweep}
            sweeping={sweeping}
            isExisting={load.existing_load_flag}
            subtitle={
              [load.state, load.eia_ba_code].filter(Boolean).join(" · ") ||
              undefined
            }
            spatial={spatialSection}
            offsets={offsetsSection}
          />
        </Section>
      )}

      {/* ——— Classic view (existing cards) ——— */}
      {!rangeView && (
        <>
          {/* Lead: footprint as harm + which generators served this load */}
          {hasResults &&
            summary?.grid_supply &&
            (summary.grid_supply.meta?.grid_import_mwh ?? 0) > 0 && (
              <Section>
                <LoadGridSupplyPanel
                  portfolioName={load.load_name}
                  summary={summary}
                />
              </Section>
            )}

          {/* Net composition */}
          {hasResults && summary && (
            <Section>
              <LoadImpactSummaryCard
                portfolioName={load.load_name}
                summary={summary}
                dispatch={dispatch}
                baCode={load.eia_ba_code}
                scenarios={run?.scenarios}
              />
            </Section>
          )}

          {/* Load Serving Stats — unified annual-profile + mix/coverage card */}
          {hasResults && dispatch && (
            <Section>
              <LoadServingStatsCard
                dispatch={dispatch}
                daily={dailyDispatch || []}
              />
            </Section>
          )}

          {/* Full impact breakdown — 19 ReCiPe/USEtox categories incl. soil/water */}
          {hasResults &&
            (summary?.grid_supply?.impacts || summary?.ng_impacts?.impacts) && (
              <Section>
                <LoadImpactExplorer summary={summary!} />
              </Section>
            )}

          {hasResults &&
            (summary?.grid_supply?.emissions ||
              summary?.ng_impacts?.emissions) && (
              <Section>
                <LoadEmissionsCard summary={summary} />
              </Section>
            )}

          {spatialSection}

          {offsetsSection}
        </>
      )}

      <LoadOffsetModal
        isOpen={offsetModalOpen}
        onClose={() => {
          setOffsetModalOpen(false);
          setEditingOffset(null);
        }}
        loadId={load.load_id}
        initial={editingOffset}
      />

      <RerunConfirmDialog
        isOpen={rerunOpen}
        onClose={() => setRerunOpen(false)}
        changelog={run?.changelog}
        resultModelVersion={run?.result_model_version}
        currentModelVersion={run?.current_model_version}
        onConfirm={async () => {
          if (!token || !loadId) return;
          await queueLoadRun(loadId, token);
          qc.invalidateQueries({ queryKey: ["load", loadId] });
        }}
      />
    </PageLayout>
    </RangeModeProvider>
  );
}
